import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  CalendarDays,
  Wallet,
  ChevronRight,
  ShieldAlert,
  Shirt,
  Clock,
  ArrowRight,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { usePortalAuth } from "./PortalAuthContext";
import { useLocalizedDates } from "./i18n/dates";

const fmtFt = (v) => `${Math.round(v || 0).toLocaleString("hu-HU")} Ft`;
const fmtHu = (n) => Number(n || 0).toLocaleString("hu-HU");
const DEFAULT_MONTHLY_TARGET = 168;

function greetingKey() {
  const h = new Date().getHours();
  if (h < 11) return "dashboard.greeting_morning";
  if (h < 18) return "dashboard.greeting_day";
  return "dashboard.greeting_evening";
}

function firstName(name) {
  if (!name) return "";
  const parts = name.trim().split(/\s+/);
  return parts[parts.length - 1];
}

function relativeDay(iso, t) {
  if (!iso) return "";
  const d = new Date(iso);
  const days = Math.round((d - new Date(new Date().toDateString())) / 86_400_000);
  if (days === 0) return t("common.today");
  if (days === 1) return t("common.tomorrow");
  if (days > 1 && days < 7) return t("dashboard.in_days", { count: days });
  return `${d.getMonth() + 1}.${String(d.getDate()).padStart(2, "0")}.`;
}

function minsUntil(dateIso, timeStr) {
  if (!dateIso || !timeStr) return null;
  const [h, m] = timeStr.split(":").map(Number);
  const start = new Date(dateIso + "T00:00:00");
  start.setHours(h || 0, m || 0, 0, 0);
  return Math.round((start - new Date()) / 60000);
}

function fmtCountdown(mins) {
  if (mins == null) return "";
  const abs = Math.abs(mins);
  if (abs < 60) return `${abs} perc`;
  const h = Math.floor(abs / 60);
  const m = abs % 60;
  if (h < 24) return m > 0 ? `${h} óra ${m} perc` : `${h} óra`;
  const d = Math.floor(h / 24);
  return `${d} nap`;
}

export default function PortalDashboard() {
  const { employee } = usePortalAuth();
  const { t } = useTranslation();
  const { days, months } = useLocalizedDates();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/portal/dashboard");
        setData(data);
      } catch (e) {
        toast.error(formatApiError(e?.response?.data?.detail) || t("common.error"));
      } finally {
        setLoading(false);
      }
    })();
  }, [t]);

  const today = new Date();
  const dateStr = `${days[today.getDay()]}, ${months[today.getMonth()]} ${today.getDate()}.`;

  return (
    <div className="space-y-8 px-4 pb-8 pt-6 md:px-6 md:pt-8">
      {/* Greeting */}
      <header>
        <p className="text-[14px] text-zinc-500">{dateStr}</p>
        <h1 className="mt-1.5 text-[32px] font-semibold leading-tight tracking-tight md:text-[36px]">
          {t(greetingKey())}, {firstName(data?.name || employee?.name) || "—"}
        </h1>
      </header>

      {/* Next shift hero */}
      <NextShiftCard shift={data?.next_shift} loading={loading} t={t} days={days} months={months} />

      {/* Activity */}
      <Section title={t("schedule.upcoming")} link={{ to: "/dolgozo/beosztas", label: t("nav.schedule") }}>
        <ActivityCard
          hours={data?.month?.hours || 0}
          shifts={data?.month?.shifts || 0}
          target={data?.month?.target_hours || DEFAULT_MONTHLY_TARGET}
          monthLabel={data?.month?.start_date ? monthLabelFromIso(data.month.start_date, months) : ""}
        />
      </Section>

      {/* Finances */}
      <Section title={t("finances.title")} link={{ to: "/dolgozo/penzugyek", label: t("common.details") }}>
        <FinancesCard
          pending={data?.salary?.pending_amount || 0}
          pendingMonth={data?.salary?.pending_month}
          lastPaid={data?.salary?.last_paid_amount || 0}
          lastPaidMonth={data?.salary?.last_paid_month}
          bonusAmount={data?.bonus_this_month?.amount || 0}
          bonusCount={data?.bonus_this_month?.count || 0}
        />
      </Section>

      {/* Disciplinary alert */}
      {data?.disciplinary_total > 0 && (
        <Link
          to="/dolgozo/profil"
          className="flex items-center gap-3 rounded-2xl bg-white p-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] transition-shadow hover:shadow-[0_4px_12px_-4px_rgba(0,0,0,0.08)] dark:bg-zinc-900"
        >
          <div className="grid h-10 w-10 place-items-center rounded-full bg-red-50 text-red-600 dark:bg-red-950/40 dark:text-red-400">
            <ShieldAlert className="h-4 w-4" strokeWidth={2} />
          </div>
          <div className="flex-1">
            <p className="text-[14px] font-medium tracking-tight">{t("dashboard.disciplinary_record")}</p>
            <p className="mt-0.5 text-[12.5px] text-zinc-500">
              {t("dashboard.disciplinary_details", { count: data.disciplinary_total })}
            </p>
          </div>
          <ChevronRight className="h-5 w-5 text-zinc-300" strokeWidth={2} />
        </Link>
      )}

      {/* Quick actions */}
      <Section title={t("dashboard.quick_access")}>
        <div className="grid grid-cols-3 gap-3">
          <QuickAction to="/dolgozo/beosztas" label={t("nav.schedule")} icon={CalendarDays} />
          <QuickAction to="/dolgozo/penzugyek" label={t("nav.finances")} icon={Wallet} />
          <QuickAction to="/dolgozo/munkaruha" label={t("nav.workwear")} icon={Shirt} />
        </div>
      </Section>
    </div>
  );
}

function Section({ title, link, children }) {
  return (
    <section>
      <div className="mb-3 flex items-center justify-between px-1">
        <h2 className="text-[18px] font-semibold tracking-tight">{title}</h2>
        {link && (
          <Link
            to={link.to}
            className="inline-flex items-center gap-0.5 text-[13.5px] font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300"
          >
            {link.label}
            <ChevronRight className="h-4 w-4" strokeWidth={2} />
          </Link>
        )}
      </div>
      {children}
    </section>
  );
}

function NextShiftCard({ shift, loading, t, days, months }) {
  if (loading) {
    return <div className="h-[200px] animate-pulse rounded-3xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04)] dark:bg-zinc-900" />;
  }
  if (!shift) {
    return (
      <div className="rounded-3xl bg-white p-7 text-center shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
        <div className="mx-auto grid h-12 w-12 place-items-center rounded-full bg-zinc-100 text-zinc-400 dark:bg-zinc-800">
          <CalendarDays className="h-5 w-5" strokeWidth={1.8} />
        </div>
        <p className="mt-4 text-[15px] font-medium tracking-tight">{t("dashboard.no_published_shift")}</p>
        <p className="mt-1 text-[13px] text-zinc-500">
          {t("dashboard.new_schedule_notify")}
        </p>
      </div>
    );
  }
  const d = new Date(shift.date);
  const minsToStart = minsUntil(shift.date, shift.start_time);
  const isUpcoming = minsToStart != null && minsToStart > 0;

  return (
    <Link
      to="/dolgozo/beosztas"
      data-testid="next-shift-card"
      className="block rounded-3xl bg-white p-6 shadow-[0_1px_3px_0_rgba(0,0,0,0.05),0_2px_6px_-2px_rgba(0,0,0,0.04)] transition-shadow hover:shadow-[0_8px_24px_-8px_rgba(0,0,0,0.12)] dark:bg-zinc-900 md:p-7"
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="inline-flex items-center gap-1.5 rounded-full bg-blue-50 px-2.5 py-1 dark:bg-blue-950/40">
            <Clock className="h-3 w-3 text-blue-600 dark:text-blue-400" strokeWidth={2.2} />
            <span className="text-[11.5px] font-medium text-blue-700 dark:text-blue-300">
              {t("dashboard.next_shift")}
            </span>
          </div>
          <p className="mt-3 text-[36px] font-semibold leading-none tracking-[-0.02em] md:text-[42px]">
            {relativeDay(shift.date, t)}
          </p>
          <p className="mt-2 text-[13.5px] text-zinc-500">
            {days[d.getDay()]} · {d.getFullYear()}. {months[d.getMonth()].slice(0, 3)}. {d.getDate()}.
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[15px] font-semibold tabular-nums">{shift.hours}h</p>
          {shift.unpaid_break_minutes > 0 && (
            <p className="mt-0.5 text-[11.5px] text-zinc-400">{t("dashboard.unpaid_break", { count: shift.unpaid_break_minutes })}</p>
          )}
        </div>
      </div>

      {/* Time range */}
      <div className="mt-6 flex items-end gap-3">
        <span className="font-mono text-[36px] font-light leading-none tabular-nums tracking-[-0.02em] md:text-[44px]">
          {shift.start_time}
        </span>
        <ArrowRight className="mb-2 h-4 w-4 text-zinc-300" strokeWidth={2} />
        <span className="mb-0 font-mono text-[26px] font-light leading-none tabular-nums tracking-[-0.02em] text-zinc-500 md:text-[32px]">
          {shift.end_time}
        </span>
      </div>

      {/* Day timeline */}
      <DayTimeline startTime={shift.start_time} endTime={shift.end_time} />

      {/* Footer */}
      <div className="mt-5 flex items-center justify-between">
        <p className="text-[13.5px] font-medium tracking-tight text-zinc-700 dark:text-zinc-300">
          {shift.site_name || "—"}
        </p>
        {isUpcoming && (
          <p className="text-[12.5px] text-zinc-500">
            {t("dashboard.starts_in", { value: fmtCountdown(minsToStart, t) })}
          </p>
        )}
      </div>
    </Link>
  );
}

function DayTimeline({ startTime, endTime }) {
  if (!startTime || !endTime) return null;
  const toMin = (t) => {
    const [h, m] = t.split(":").map(Number);
    return (h || 0) * 60 + (m || 0);
  };
  const startMin = toMin(startTime);
  let endMin = toMin(endTime);
  if (endMin <= startMin) endMin += 24 * 60;
  const dayMin = 24 * 60;
  const startPct = (startMin / dayMin) * 100;
  const widthPct = ((endMin - startMin) / dayMin) * 100;
  // overnight wraps — clamp to 100% from start
  const safeWidthPct = Math.min(widthPct, 100 - startPct);

  return (
    <div className="mt-5">
      <div className="relative h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div
          className="absolute h-full rounded-full bg-blue-500"
          style={{ left: `${startPct}%`, width: `${safeWidthPct}%` }}
        />
        {widthPct > safeWidthPct && (
          <div
            className="absolute h-full rounded-full bg-blue-500"
            style={{ left: 0, width: `${widthPct - safeWidthPct}%` }}
          />
        )}
      </div>
      <div className="mt-1.5 flex justify-between text-[10.5px] text-zinc-400">
        <span>00</span>
        <span>06</span>
        <span>12</span>
        <span>18</span>
        <span>24</span>
      </div>
    </div>
  );
}

function ActivityCard({ hours, shifts, target, monthLabel }) {
  const { t } = useTranslation();
  const pct = Math.max(0, Math.min(100, (hours / target) * 100));
  const surplus = hours > target ? hours - target : 0;
  return (
    <div className="rounded-2xl bg-white p-5 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900 md:p-6">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="text-[13px] text-zinc-500">
            {monthLabel || t("dashboard.this_month")}
          </p>
          <p className="mt-1.5 font-mono text-[34px] font-semibold leading-none tabular-nums tracking-[-0.02em] md:text-[40px]">
            {fmtHu(hours)}
            <span className="ml-1 text-[18px] font-medium text-zinc-400">h</span>
          </p>
          <p className="mt-2 text-[13px] text-zinc-500">
            {t("dashboard.shifts_completed", { count: shifts })}
          </p>
        </div>
        <div className="text-right">
          <p className="font-mono text-[15px] font-semibold tabular-nums text-blue-600 dark:text-blue-400">
            {Math.round(pct)}%
          </p>
          <p className="mt-1 text-[12px] text-zinc-500">
            {surplus > 0 ? t("dashboard.hours_over", { hours: fmtHu(surplus) }) : t("dashboard.hours_left", { hours: fmtHu(Math.max(0, target - hours)) })}
          </p>
          <p className="text-[11.5px] text-zinc-400">{t("dashboard.target_short", { target })}</p>
        </div>
      </div>
      <div className="mt-4 h-1.5 overflow-hidden rounded-full bg-zinc-100 dark:bg-zinc-800">
        <div
          className="h-full rounded-full bg-blue-500 transition-all"
          style={{ width: `${Math.min(100, pct)}%` }}
        />
      </div>
    </div>
  );
}

function monthLabelFromIso(iso, months) {
  if (!iso) return "";
  const [y, m] = iso.split("-").map(Number);
  return `${y}. ${months[(m || 1) - 1]}`;
}

function FinancesCard({ pending, pendingMonth, lastPaid, lastPaidMonth, bonusAmount, bonusCount }) {
  const { t } = useTranslation();
  return (
    <div className="overflow-hidden rounded-2xl bg-white shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] dark:bg-zinc-900">
      <FinRow
        label={t("dashboard.expected_payment")}
        value={fmtFt(pending)}
        sub={pendingMonth || t("dashboard.no_pending_item")}
        status={pending > 0 ? "pending" : null}
      />
      <div className="mx-5 h-px bg-zinc-100 dark:bg-zinc-800" />
      <FinRow
        label={t("dashboard.last_payment")}
        value={fmtFt(lastPaid)}
        sub={lastPaidMonth || "—"}
        status={lastPaid > 0 ? "paid" : null}
      />
      <div className="mx-5 h-px bg-zinc-100 dark:bg-zinc-800" />
      <FinRow
        label={t("dashboard.bonus_this_month")}
        value={fmtFt(bonusAmount)}
        sub={t("drafts.items_count", { count: bonusCount })}
      />
    </div>
  );
}

function FinRow({ label, value, sub, status }) {
  const dot =
    status === "paid"
      ? "bg-emerald-500"
      : status === "pending"
      ? "bg-amber-500"
      : null;
  return (
    <div className="flex items-center justify-between gap-4 px-5 py-4">
      <div>
        <p className="text-[13px] text-zinc-500">{label}</p>
        <p className="mt-1 text-[12px] text-zinc-400">{sub}</p>
      </div>
      <div className="flex items-center gap-2">
        {dot && <span className={`h-1.5 w-1.5 rounded-full ${dot}`} />}
        <p className="font-mono text-[18px] font-semibold tabular-nums tracking-tight">{value}</p>
      </div>
    </div>
  );
}

function QuickAction({ to, label, icon: Icon }) {
  return (
    <Link
      to={to}
      className="flex flex-col items-center gap-2 rounded-2xl bg-white px-3 py-4 shadow-[0_1px_3px_0_rgba(0,0,0,0.04),0_1px_2px_-1px_rgba(0,0,0,0.02)] transition-shadow hover:shadow-[0_4px_14px_-4px_rgba(0,0,0,0.1)] dark:bg-zinc-900"
    >
      <div className="grid h-11 w-11 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-400">
        <Icon className="h-5 w-5" strokeWidth={2} />
      </div>
      <span className="text-[12.5px] font-medium tracking-tight">{label}</span>
    </Link>
  );
}
